/**
 * 将字符串转换为十六进制颜色值
 * @param {string} str - 输入字符串
 * @param {number} seed - 随机种子，用于生成不同的颜色变化
 * @returns {string} - 十六进制颜色值 (#xxxxxx)
 */
export const stringToColor = (str, seed = 42) => {
  if (!str) return '#6c757d'; // 默认灰色
  
  // 改进的哈希函数 - 使用多轮哈希和种子
  let hash1 = seed;
  let hash2 = seed * 31;
  
  // 第一轮哈希
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash1 = ((hash1 << 5) - hash1 + char) & 0xffffffff;
    hash2 = ((hash2 << 3) - hash2 + char * 7) & 0xffffffff;
  }
  
  // 第二轮哈希 - 增加复杂性
  hash1 = ((hash1 ^ (hash1 >>> 16)) * 0x85ebca6b) & 0xffffffff;
  hash1 = ((hash1 ^ (hash1 >>> 13)) * 0xc2b2ae35) & 0xffffffff;
  hash1 = (hash1 ^ (hash1 >>> 16)) & 0xffffffff;
  
  hash2 = ((hash2 ^ (hash2 >>> 15)) * 0x9e3779b9) & 0xffffffff;
  hash2 = ((hash2 ^ (hash2 >>> 12)) * 0x6c078965) & 0xffffffff;
  hash2 = (hash2 ^ (hash2 >>> 15)) & 0xffffffff;
  
  // 混合两个哈希值生成RGB
  const combinedHash = (hash1 ^ hash2) & 0xffffffff;
  
  // 使用不同的位移生成RGB值，确保更好的分布
  const r = (combinedHash >>> 0) & 255;
  const g = (combinedHash >>> 8) & 255;
  const b = (combinedHash >>> 16) & 255;
  
  // 调整颜色范围，确保足够的对比度和饱和度
  const adjustedR = Math.max(60, Math.min(220, r));
  const adjustedG = Math.max(60, Math.min(220, g));
  const adjustedB = Math.max(60, Math.min(220, b));
  
  // 转换为十六进制
  const toHex = (num) => {
    const hex = num.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(adjustedR)}${toHex(adjustedG)}${toHex(adjustedB)}`;
};

/**
 * 生成渐变色背景
 * @param {string} str - 输入字符串
 * @returns {string} - CSS渐变背景
 */
export const stringToGradient = (str) => {
  if (!str) return 'linear-gradient(135deg, #6c757d 0%, #495057 100%)';
  
  const baseColor = stringToColor(str);
  
  // 生成稍微不同的第二个颜色
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 3) - hash);
  }
  
  const r = parseInt(baseColor.slice(1, 3), 16);
  const g = parseInt(baseColor.slice(3, 5), 16);
  const b = parseInt(baseColor.slice(5, 7), 16);
  
  // 创建第二个颜色（稍微调暗）
  const r2 = Math.max(30, r - 30);
  const g2 = Math.max(30, g - 30);
  const b2 = Math.max(30, b - 30);
  
  const toHex = (num) => {
    const hex = num.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  const secondColor = `#${toHex(r2)}${toHex(g2)}${toHex(b2)}`;
  
  return `linear-gradient(135deg, ${baseColor} 0%, ${secondColor} 100%)`;
};

/**
 * 根据背景颜色计算合适的文字颜色
 * @param {string} backgroundColor - 背景颜色 (#xxxxxx)
 * @returns {string} - 文字颜色 (white 或 black)
 */
export const getTextColor = (backgroundColor) => {
  if (!backgroundColor) return 'white';
  
  // 移除 # 符号
  const hex = backgroundColor.replace('#', '');
  
  // 转换为RGB
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  
  // 计算亮度 (使用相对亮度公式)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // 如果亮度大于0.5，使用黑色文字，否则使用白色文字
  return luminance > 0.5 ? '#333333' : '#ffffff';
}; 